/**
 * Helper de cache localStorage pour éviter le « Flash of Stale Content »
 * (FoSC) au retour sur une page avec pre-rendering Puppeteer.
 *
 * Le pre-render Puppeteer génère un HTML statique contenant les bonnes
 * données. Mais lors de l'hydratation React, `useState(null)` réécrit
 * brièvement le DOM avec un état vide tant que l'API n'a pas répondu.
 * Ce helper persiste la dernière valeur connue dans localStorage et la
 * restaure comme état initial.
 *
 * Caractéristiques :
 *   - Resilient (try/catch silencieux si localStorage indisponible)
 *   - SSR-safe (vérifie `typeof window`)
 *   - TTL configurable (par défaut 24h)
 */

const PREFIX = 'mc:cache:';

/**
 * Lit une valeur cachée pour la clé donnée, en respectant le TTL.
 * Retourne `fallback` si pas de cache ou cache expiré.
 *
 * @template T
 * @param {string} key   - Identifiant de la donnée (ex: "hero")
 * @param {T}      fallback - Valeur par défaut si rien en cache
 * @param {number} ttlMs - Durée de vie en millisecondes (défaut : 24h)
 * @returns {T}
 */
export function readCached(key, fallback = null, ttlMs = 24 * 60 * 60 * 1000) {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(PREFIX + key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    const ageMs = Date.now() - (parsed?._cachedAt || 0);
    if (ageMs > ttlMs) return fallback;
    return parsed?.data ?? fallback;
  } catch {
    return fallback;
  }
}

/**
 * Persiste une valeur dans le cache localStorage avec timestamp.
 *
 * @param {string} key   - Identifiant de la donnée
 * @param {*}      data  - Donnée à persister (sérialisable JSON)
 */
export function writeCached(key, data) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      PREFIX + key,
      JSON.stringify({ data, _cachedAt: Date.now() }),
    );
  } catch {
    /* Quota exceeded ou indisponible → silence */
  }
}

/**
 * Supprime une entrée du cache (utile quand on connaît une donnée
 * obsolète, ex: après un logout).
 *
 * @param {string} key
 */
export function clearCached(key) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(PREFIX + key);
  } catch {
    /* silence */
  }
}

/**
 * Vide tout le cache géré par ce helper (utile au logout pour purger
 * les données utilisateur sensibles : gamification, progression…).
 */
export function clearAllCached() {
  if (typeof window === 'undefined') return;
  try {
    Object.keys(window.localStorage)
      .filter((k) => k.startsWith(PREFIX))
      .forEach((k) => window.localStorage.removeItem(k));
  } catch {
    /* silence */
  }
}
